import React, { useState, useEffect, useMemo, useRef } from "react";
import "./style.css";
import { Form, Alert, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useFormik } from "formik";
import { teslaApi } from "../../Services/teslaApi";
import { getLatestIndicativeEnergyPrices } from "../../Services/energyPriceApi";
import { transformEnergyPriceFromApi } from "../../utils/EnergyPrice";
import { parseBrazilianNumber } from "../../utils/utilityFunctions";
import { useSimulatorUser } from "./hooks/useSimulatorUser";
import { useSimulationFieldHandlers } from "./hooks/useSimulationFieldHandlers";
import { useProposalGeneration } from "./hooks/useProposalGeneration";
import {
  filterSimulationHistory,
  useSimulationHistory,
} from "./hooks/useSimulationHistory";
import { SimulationDataSummary } from "./results/SimulationDataSummary";
import { SimulationResults } from "./results/SimulationResults";
import { HistoryModal } from "./HistoryModal";
import { SimulationTypeSelector } from "./SimulationTypeSelector";
import { FormValidationErrors } from "./FormValidationErrors";
import { ClientDataSection } from "./sections/ClientDataSection";
import { InvoiceDataSection } from "./sections/InvoiceDataSection";
import { BatterySection } from "./sections/BatterySection";
import { GeneratorSection } from "./sections/GeneratorSection";
import { TaxesSection } from "./sections/TaxesSection";
import { FreeMarketSection } from "./sections/FreeMarketSection";
import { GDSection } from "./sections/GDSection";
import { SimulationConfigSection } from "./sections/SimulationConfigSection";
import { SelfProductionSection } from "./sections/SelfProductionSection";
import { PdfDocumentParams } from "./pdfDocuments";
import { buildInitialValues, simulationValidationSchema } from "./formSchema";
import { getApiErrorMessage } from "./apiError";
import { transformValues, withFilteredMedicaoCsv } from "./transformValues";
import { isInvestmentProjectionSimulation } from "./simulationGuards";
import { startInvoiceUploads } from "./invoiceUploads";
import {
  FormValues,
  SIMULATION_SIDE_TYPES,
  SimulationRequest,
  EconomyCompareResult,
  Distribuidora,
  EnergyPriceMatrix,
} from "./types";

export { parseInitialYearSelectValue } from "./formSchema";

const FormSimulacao = ({ fortlev }: { fortlev?: boolean }) => {
  const [distribuidoras, setDistribuidoras] = useState<Distribuidora[]>([]);
  const [
    simulationResult,
    setSimulationResult,
  ] = useState<EconomyCompareResult | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewSimulacaoData, setViewSimulacaoData] = useState(false);
  const [simulacaoSelected, setSimulacaoSelected] = useState("");
  const [historyFilter, setHistoryFilter] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [
    energyPriceMatrix,
    setEnergyPriceMatrix,
  ] = useState<EnergyPriceMatrix | null>(null);
  const submitHandlerRef = useRef<(values: FormValues) => Promise<void>>(
    async () => {}
  );
  const {
    userEmail,
    proposalPermission,
    internalPartnerPermission,
    userPermission,
    companyLogo,
  } = useSimulatorUser(fortlev);

  useEffect(() => {
    const fetchData = async () => {
      const { data } = await teslaApi.get("distribuidoras");
      setDistribuidoras(
        data.sort((a: Distribuidora, b: Distribuidora) =>
          a.name.localeCompare(b.name)
        )
      );
    };

    // call the function
    fetchData()
      // make sure to catch any error
      .catch(console.error);
  }, []);

  useEffect(() => {
    const fetchIndicativeEnergyPrices = async () => {
      try {
        const latestIndicativePrices = await getLatestIndicativeEnergyPrices();
        const transformedPrices = transformEnergyPriceFromApi(
          latestIndicativePrices
        );

        setEnergyPriceMatrix(transformedPrices);
      } catch (error) {
        setEnergyPriceMatrix(null);
      }
    };

    fetchIndicativeEnergyPrices();
  }, []);

  useEffect(() => {
    if (simulacaoSelected !== "") {
      // eslint-disable-next-line no-unused-expressions
      const element = document.getElementById("simuladorId");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [simulacaoSelected]);

  const handleViewData = () => {
    if (viewSimulacaoData) setViewSimulacaoData(false);
    else setViewSimulacaoData(true);
  };

  const formik = useFormik<FormValues>({
    initialValues: buildInitialValues(fortlev),
    validationSchema: proposalPermission
      ? simulationValidationSchema
      : simulationValidationSchema.omit([
          "lcoe",
          "localization",
          "modules",
          "inverter",
          "structure",
          "items",
        ]),
    onSubmit: (values: FormValues) => submitHandlerRef.current(values),
  });
  const buttonSelected = formik.values.simulationType;

  // O setFieldValue do formik valida contra os valores do último render, então
  // handlers que alteram vários campos de uma vez (restaurar histórico, trocar
  // distribuidora) deixam erros defasados nos campos que acabaram de ser
  // preenchidos. Revalidar quando os valores mudam mantém os erros coerentes.
  const { validateForm } = formik;
  useEffect(() => {
    validateForm();
  }, [formik.values, validateForm]);

  const demandaValid =
    formik.values.demandaPonta !== "0" || formik.values.demandaForaPonta !== "";

  const tipoContratacao = formik.values.tipoContratacao;
  const simulationType = formik.values.simulationType;
  const construindoUsina = isInvestmentProjectionSimulation(simulacaoSelected);
  const {
    history,
    clientHistory,
    loadingHistory,
    fetchHistory,
  } = useSimulationHistory(formik.values.cnpj, internalPartnerPermission);
  const filteredHistory = useMemo(
    () => (modalShow ? filterSimulationHistory(history, historyFilter) : []),
    [history, historyFilter, modalShow]
  );

  const {
    restoreSimulationHistory,
    refreshIndicativePrices,
    changeTipoContratacao,
    calculateEnergyPrice,
    handleDefaultPrices,
    resetGrupoTarifario,
    resetTHSWithGrupoTarifario,
    changeContractTypeWithDemandaForaPonta,
    changeContractTypeWithDemandaPonta,
    resetDiscounWithtUltrapassagemForaPontaType,
    resetDiscountWithUltrapassagemPontaType,
    resetSimultaneidade,
    changeSituacaoUsina,
    resetTHS,
    changeSimulationPeriod,
    resetEnergyPrice,
    resetScarcity,
    resetEncargos,
    resetAbateICMS,
    resetGenerationConsume,
    resetGenerationCost,
    resetDieselCost,
    addItem,
    removeItem,
    handleItemChange,
    handleFileChange,
  } = useSimulationFieldHandlers({
    formik,
    distribuidoras,
    energyPriceMatrix,
  });

  useEffect(() => {
    if (!formik.values.uf) return;

    refreshIndicativePrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [energyPriceMatrix]);

  const sanitizeFileNamePart = (value: string) =>
    value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .trim();

  const fileName = formik.values.nomeCliente
    ? sanitizeFileNamePart(formik.values.nomeCliente).concat(` viabilidade.pdf`)
    : `${formik.values.cnpj.replace(/[^a-zA-Z0-9]/g, "")} viabilidade.pdf`;

  const fileNamePresentation = formik.values.nomeCliente
    ? sanitizeFileNamePart(formik.values.nomeCliente).concat(
        ` apresentacao viabilidade.pdf`
      )
    : `${formik.values.cnpj.replace(
        /[^a-zA-Z0-9]/g,
        ""
      )} apresentacao viabilidade`.concat(`.pdf`);

  const fileProposalName = formik.values.nomeCliente
    ? sanitizeFileNamePart(formik.values.nomeCliente).concat(
        ` proposta autoproducao.pdf`
      )
    : `${formik.values.cnpj.replace(
        /[^a-zA-Z0-9]/g,
        ""
      )} proposta autoproducao`.concat(`.pdf`);

  const simulatorData = {
    ...formik.values,
    distribuidora: formik.values.distribuidora,
    ths: formik.values.ths,
    grupo: formik.values.grupoTarifario,
    desconto: formik.values.desconto,
    custoGestao: formik.values.custoGestao,
    cliente: formik.values.nomeCliente,
    pis: formik.values.pis,
    icms: formik.values.icms,
    cofins: formik.values.cofins,
    demandaForaPonta: formik.values.demandaForaPonta,
    demandaPonta: formik.values.demandaPonta,
    consumoPonta: formik.values.consumoPonta,
    consumoForaPonta: formik.values.consumoForaPonta,
  };

  const infoUsina = {
    usinaLocal: formik.values.local,
    simultaneidade: formik.values.simultaneidade,
    geracao: parseBrazilianNumber(formik.values.geracao),
    demandaUsina: parseBrazilianNumber(formik.values.demandaUsina),
    potenciaUsina: parseBrazilianNumber(formik.values.potenciaUsina),
    custoProjeto: parseBrazilianNumber(formik.values.custoProjeto),
  };

  const {
    proposalData,
    screenShotChart,
    selectedFile,
    fileLoading,
    generateProposalData,
    handleFileSelect,
    handleUpload,
    handleUploadMerx,
    handleUploadFortlev,
  } = useProposalGeneration({
    formik,
    userEmail,
    simulacaoSelected,
    setShowAlert,
  });

  submitHandlerRef.current = async (values: FormValues) => {
    setShowAlert(false);
    setErrorMessage(null);
    setLoading(true);
    setSimulacaoSelected("");
    try {
      if (
        proposalPermission &&
        values.generateProposal &&
        (values.simulationType === "APE" || values.simulationType === "MLXAPE")
      ) {
        generateProposalData();
      }
      startInvoiceUploads({
        userPermission,
        fortlev,
        handleUpload,
        handleUploadMerx,
        handleUploadFortlev,
      });

      const payload = withFilteredMedicaoCsv(
        transformValues(values),
        values.pontoMedicaoBess
      );
      if (payload === null) {
        setLoading(false);
        setErrorMessage(
          `O ponto de medição "${values.pontoMedicaoBess}" não foi encontrado no CSV de medição enviado. Selecione outro ponto ou envie um CSV que contenha esse ponto.`
        );
        setShowAlert(true);
        return;
      }

      const isBess =
        values.simulationType === "MLBESS" ||
        values.simulationType === "APEBESS";

      const request: SimulationRequest = {
        firstSimulationType: SIMULATION_SIDE_TYPES[values.simulationType].first,
        secondSimulationType:
          SIMULATION_SIDE_TYPES[values.simulationType].second,
        firstPayload: isBess ? { ...payload, isBateria: false } : payload,
        secondPayload: payload,
      };

      const result = await teslaApi.post("economies/compare", request);
      setSimulationResult(result.data);
      setSimulacaoSelected(values.simulationType);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setErrorMessage(getApiErrorMessage(e));
      setShowAlert(true);
    }
  };

  const pdfDocumentParams: PdfDocumentParams = {
    simulationResult,
    simulacaoSelected,
    simulatorData,
    infoUsina,
    screenShotChart,
    desconto: formik.values.desconto,
    tipoContratacao,
    companyLogo,
  };

  return (
    <div className="container">
      <div id="simuladorId" className="container-formSimulacao">
        <div
          className="simulacao-header-title"
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            {simulacaoSelected === "" ? (
              <>
                <h1>Simulação de economia</h1>
                <h2>
                  Preencha o formulário abaixo e tenha em mãos um estudo
                  comparativo entre Autoprodutor de energia, Mercado Cativo e
                  Mercado Livre
                </h2>
              </>
            ) : (
              <>
                <h1>Resultado da simulação de economia</h1>
                <h2>
                  Este não é um estudo final. Ele não considera todas as
                  variáveis, caso precise de um estudo mais aprofundado, entre
                  em contato conosco
                </h2>
              </>
            )}
          </div>

          {!fortlev && (
            <div>
              <button
                onClick={async () => {
                  setModalShow(true);
                  await fetchHistory();
                }}
                className="simulacao-button-selected"
              >
                Ver histórico
              </button>
            </div>
          )}
        </div>

        {modalShow && (
          <HistoryModal
            show={modalShow}
            onHide={() => {
              setModalShow(false);
            }}
            loading={loadingHistory}
            history={history}
            filteredHistory={filteredHistory}
            filter={historyFilter}
            onFilterChange={setHistoryFilter}
            canRestore={simulacaoSelected === ""}
            onRestore={(item) => {
              restoreSimulationHistory(item);
              setModalShow(false);
            }}
          />
        )}

        {simulacaoSelected === "" ? (
          <Form
            className="simulacao-form"
            noValidate
            onSubmit={formik.handleSubmit}
          >
            <SimulationTypeSelector
              selected={buttonSelected}
              fortlev={fortlev}
              onSelect={(option) => {
                formik.setFieldValue("simulationType", option.type);
                formik.setFieldValue("calc", option.calc);

                const isBess =
                  option.type === "MLBESS" || option.type === "APEBESS";
                formik.setFieldValue("isBateria", isBess);

                if (option.type === "ML" || option.type === "MLBESS") {
                  formik.setFieldValue("geracao", "0");
                } else {
                  changeTipoContratacao({ target: { value: "atacado" } });
                }

                if (option.type === "APEBESS") {
                  changeSituacaoUsina({ target: { value: "EXISTENTE" } });
                }
              }}
            />

            <ClientDataSection
              formik={formik}
              distribuidoras={distribuidoras}
              clientHistory={clientHistory}
              internalPartnerPermission={internalPartnerPermission}
              onShowHistory={() => {
                setModalShow(true);
              }}
              resetGrupoTarifario={resetGrupoTarifario}
              resetTHSWithGrupoTarifario={resetTHSWithGrupoTarifario}
              resetTHS={resetTHS}
            />

            <InvoiceDataSection
              formik={formik}
              changeContractTypeWithDemandaPonta={
                changeContractTypeWithDemandaPonta
              }
              changeContractTypeWithDemandaForaPonta={
                changeContractTypeWithDemandaForaPonta
              }
              resetDiscountWithUltrapassagemPontaType={
                resetDiscountWithUltrapassagemPontaType
              }
              resetDiscounWithtUltrapassagemForaPontaType={
                resetDiscounWithtUltrapassagemForaPontaType
              }
            />

            <BatterySection formik={formik} buttonSelected={buttonSelected} />

            {simulationType !== "MLXAPE" &&
              simulationType !== "MLBESS" &&
              simulationType !== "APEBESS" && (
                <GeneratorSection
                  formik={formik}
                  resetGenerationConsume={resetGenerationConsume}
                  resetDieselCost={resetDieselCost}
                  resetGenerationCost={resetGenerationCost}
                />
              )}

            <FreeMarketSection
              formik={formik}
              demandaValid={demandaValid}
              buttonSelected={buttonSelected}
              calculateEnergyPrice={calculateEnergyPrice}
              changeTipoContratacao={changeTipoContratacao}
              changeSimulationPeriod={changeSimulationPeriod}
              resetEnergyPrice={resetEnergyPrice}
              handleDefaultPrices={handleDefaultPrices}
              resetScarcity={resetScarcity}
              resetEncargos={resetEncargos}
            />

            {(simulationType === "APE" ||
              simulationType === "MLXAPE" ||
              simulationType === "APEBESS") && (
              <SelfProductionSection
                formik={formik}
                buttonSelected={buttonSelected}
                proposalPermission={proposalPermission}
                changeSituacaoUsina={changeSituacaoUsina}
                resetSimultaneidade={resetSimultaneidade}
                handleFileChange={handleFileChange}
                handleItemChange={handleItemChange}
                addItem={addItem}
                removeItem={removeItem}
              />
            )}

            <TaxesSection formik={formik} />

            {simulationType === "GDXAPE" && (
              <GDSection
                formik={formik}
                buttonSelected={buttonSelected}
                resetSimultaneidade={resetSimultaneidade}
                resetAbateICMS={resetAbateICMS}
              />
            )}

            <SimulationConfigSection formik={formik} />

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              {selectedFile && (
                <span
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    color: "#7e7e82",
                    border: "1px",
                    borderRadius: "16px",
                    padding: "6px 12px",
                    width: "max-content",
                    margin: "5px auto",
                    fontSize: 13.5,
                  }}
                >
                  {selectedFile.name}
                </span>
              )}

              {!userPermission && !fortlev ? (
                <div className="send-invoice-area" translate="no">
                  <span className="send-btn" style={{ textAlign: "center" }}>
                    SELECIONE A FATURA
                    <input
                      className="input-file"
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      accept="application/pdf"
                    />
                  </span>

                  <button
                    disabled={loading || fileLoading || selectedFile === null}
                    style={{ border: 0 }}
                    className="send-btn-button simulacao-submit"
                    type="submit"
                  >
                    <span className="simulacao-submit-content">
                      {(loading || fileLoading) && (
                        <Spinner animation="border" size="sm" variant="light" />
                      )}
                      {loading || fileLoading
                        ? "SIMULANDO..."
                        : "SIMULAR ECONOMIA"}
                    </span>
                    {!selectedFile && (
                      <OverlayTrigger
                        overlay={
                          <Tooltip id="tooltip">
                            É necessário enviar a fatura para fazer a simulação
                          </Tooltip>
                        }
                        placement="bottom"
                        delay={{ show: 300, hide: 150 }}
                      >
                        <div
                          style={{
                            zIndex: 1,
                            position: "absolute",
                            top: "0px",
                            left: "0px",
                            width: "100%",
                            height: "100%",
                          }}
                        ></div>
                      </OverlayTrigger>
                    )}
                  </button>
                </div>
              ) : (
                <button
                  className={
                    !fortlev
                      ? "simulacao-button-selected simulacao-submit"
                      : "simulacao-button-fortlev-selected simulacao-submit"
                  }
                  type="submit"
                  disabled={loading}
                >
                  <span className="simulacao-submit-content">
                    {loading && (
                      <Spinner animation="border" size="sm" variant="light" />
                    )}
                    {loading ? "SIMULANDO..." : "SIMULAR ECONOMIA"}
                  </span>
                </button>
              )}

              <FormValidationErrors
                errors={formik.errors}
                submitCount={formik.submitCount}
                isValidating={formik.isValidating}
              />

              {showAlert && (
                <div className="simulacao-error">
                  <Alert
                    variant="danger"
                    onClose={() => setShowAlert(false)}
                    dismissible
                  >
                    {errorMessage ??
                      "Erro, verifique se todos os dados estão preenchidos corretamente e tente novamente!"}
                  </Alert>
                </div>
              )}
            </div>
          </Form>
        ) : (
          <SimulationDataSummary
            values={formik.values}
            simulationResult={simulationResult}
            simulacaoSelected={simulacaoSelected}
            viewSimulacaoData={viewSimulacaoData}
            onToggleView={handleViewData}
            onEdit={() => {
              setSimulacaoSelected("");
              setViewSimulacaoData(false);
            }}
          />
        )}

        <br />

        <SimulationResults
          values={formik.values}
          simulationResult={simulationResult}
          simulacaoSelected={simulacaoSelected}
          construindoUsina={construindoUsina}
          fortlev={fortlev}
          pdfDocumentParams={pdfDocumentParams}
          fileName={fileName}
          fileNamePresentation={fileNamePresentation}
          fileProposalName={fileProposalName}
          screenShotChart={screenShotChart}
          proposalData={proposalData}
          proposalPermission={proposalPermission}
        />
      </div>
    </div>
  );
};

export default FormSimulacao;
